import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Lang, changeLanguage, currentLang } from '../i18n';
import { t } from '../i18n';

// ============================================
// LanguageContext (2026-09-17) – nach dem Muster von ThemeContext.
// Ein Sprachwechsel speichert die Wahl und lädt die Seite neu (siehe i18n/index.ts),
// damit auch Label-Konstanten auf Modulebene in der neuen Sprache stehen.
// ============================================

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => Promise<void>;
  toggleLang: () => Promise<void>;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(() => currentLang());

  const setLang = useCallback(async (next: Lang) => {
    if (next === lang) return;
    await changeLanguage(next);
    setLangState(next);
  }, [lang]);

  const toggleLang = useCallback(() => setLang(lang === 'de' ? 'en' : 'de'), [lang, setLang]);

  const value = useMemo(() => ({ lang, setLang, toggleLang }), [lang, setLang, toggleLang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error(t('web.context.language-context.uselanguage-muss-innerhalb-von-languagep'));
  return ctx;
};
